// The one provider logo/monogram badge, shared by chat + evals.
import { getProviderColor, getProviderLogo } from "@/lib/provider-registry";
import { cn } from "@/lib/chat-utils";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { useScenarioHostTheme } from "@/contexts/scenario-client-style-context";

interface ProviderLogoProps {
  provider: string;
  /** Box/img size — twMerge lets callers override the default `h-3 w-3`. */
  className?: string;
  /** Monogram letter size — override alongside a larger `className`. */
  letterClassName?: string;
}

export function ProviderLogo({
  provider,
  className,
  letterClassName,
}: ProviderLogoProps) {
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const scenarioHostTheme = useScenarioHostTheme();
  const resolvedThemeMode = scenarioHostTheme ?? themeMode;
  const logoSrc = getProviderLogo(provider, resolvedThemeMode);

  // No provider at all (e.g. a placeholder model while config loads):
  // render nothing rather than a misleading badge.
  if (!provider) {
    return null;
  }

  if (!logoSrc) {
    // No logo asset — first-letter monogram.
    const letter = provider[0]?.toUpperCase() || "?";
    const color = getProviderColor(provider);
    const isBackgroundColor = color.startsWith("bg-");
    return (
      <div
        className={cn(
          "flex h-3 w-3 items-center justify-center rounded-sm",
          isBackgroundColor ? color : "bg-primary/10",
          className
        )}
      >
        <span
          className={cn(
            "font-bold text-[6px]",
            isBackgroundColor ? "text-white" : color,
            letterClassName
          )}
        >
          {letter}
        </span>
      </div>
    );
  }

  return (
    <img
      src={logoSrc}
      alt={`${provider} logo`}
      className={cn("h-3 w-3 object-contain", className)}
    />
  );
}
